import { Link, router } from "@inertiajs/react";
import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Briefcase, CheckCircle2, Circle, FileText, Loader2, Wand2 } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useAiJobStatus } from "../../hooks/useAiJobStatus";

interface AiJobRef {
  id:    number;
  state: "pending" | "running" | "ready" | "failed";
  error: string | null;
}

interface SubmissionDetail {
  id: number;
  candidate_id: number;
  candidate_name: string;
  candidate_firm: string;
  candidate_practice_area: string;
  candidate_jd_year: number | null;
  firm_name: string;
  firm_contact_name: string;
  firm_contact_email: string;
  proxy_email: string;
  job_id: number | null;
  job_title: string | null;
  job_description: string | null;
  status: string;
  submission_method: string;
  cover_letter_text: string;
  cover_letter_generated: boolean;
  candidate_approved: boolean;
  notes: string;
  submitted_at: string | null;
  submitted_by: string;
  created_at: string;
  cover_letter_job: AiJobRef | null;
}

interface Props {
  submission: SubmissionDetail;
  ai_assist: boolean;
}

const STATUS_STEPS = ["drafted", "submitted", "screening", "full_round", "offer_placed"];

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  drafted:      { label: "Drafted",      className: "bg-gray-100 text-gray-500" },
  submitted:    { label: "Submitted",    className: "bg-blue-50 text-blue-700" },
  screening:    { label: "Screening",    className: "bg-amber-50 text-amber-700" },
  full_round:   { label: "Full Round",   className: "bg-purple-50 text-purple-700" },
  offer_placed: { label: "Offer/Placed", className: "bg-green-50 text-green-700" },
  rejected:     { label: "Rejected",     className: "bg-red-50 text-red-600" },
  withdrawn:    { label: "Withdrawn",    className: "bg-gray-50 text-gray-400" },
};

const ADVANCE_LABELS: Record<string, string> = {
  drafted:    "Mark Submitted",
  submitted:  "Move to Screening",
  screening:  "Move to Full Round",
  full_round: "Mark Offer/Placed",
};

function fmt(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function SubmissionsShow({ submission, ai_assist }: Props) {
  const [coverLetter, setCoverLetter]       = useState(submission.cover_letter_text);
  const [notes, setNotes]                   = useState(submission.notes);
  const [contactName, setContactName]       = useState(submission.firm_contact_name);
  const [contactEmail, setContactEmail]     = useState(submission.firm_contact_email);
  const [proxyEmail, setProxyEmail]         = useState(submission.proxy_email);
  const [method, setMethod]                 = useState(submission.submission_method);
  const [saving, setSaving]                 = useState(false);
  const [advancing, setAdvancing]           = useState(false);
  const [jobDescription, setJobDescription] = useState("");
  const [firmListInput, setFirmListInput]   = useState("");
  const [showAdvanced, setShowAdvanced]     = useState(false);
  const [coverLetterJobId, setCoverLetterJobId] = useState<number | null>(submission.cover_letter_job?.id ?? null);

  useEffect(() => {
    setCoverLetterJobId(submission.cover_letter_job?.id ?? null);
  }, [submission.cover_letter_job?.id]);

  useEffect(() => {
    setCoverLetter(submission.cover_letter_text);
  }, [submission.cover_letter_text]);

  const { generating, timedOut: coverLetterTimedOut } = useAiJobStatus(coverLetterJobId, {
    onReady:  () => router.reload({ only: [ "submission" ] }),
    onFailed: () => router.reload({ only: [ "submission" ] }),
  });

  const currentStepIdx = STATUS_STEPS.indexOf(submission.status);
  const advanceLabel   = ADVANCE_LABELS[submission.status];
  const statusCfg      = STATUS_CONFIG[submission.status] ?? STATUS_CONFIG.drafted;

  function save() {
    setSaving(true);
    router.patch(`/submissions/${submission.id}`, {
      submission: {
        cover_letter_text:  coverLetter,
        notes:              notes,
        firm_contact_name:  contactName,
        firm_contact_email: contactEmail,
        proxy_email:        proxyEmail,
        submission_method:  method,
      },
    }, { onFinish: () => setSaving(false) });
  }

  function advance() {
    setAdvancing(true);
    router.post(`/submissions/${submission.id}/advance`, {}, {
      onFinish: () => setAdvancing(false),
    });
  }

  function generateCoverLetter() {
    router.post(`/submissions/${submission.id}/generate_cover_letter`, {
      job_description: jobDescription,
      firm_list:       firmListInput.split(",").map((f) => f.trim()).filter(Boolean),
    });
  }

  function toggleApprove() {
    router.patch(`/submissions/${submission.id}/approve`);
  }

  const isDone = ["offer_placed", "rejected", "withdrawn"].includes(submission.status);

  return (
    <AppLayout title="Submission">
      <Link href="/submissions" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft className="w-4 h-4" /> Submissions
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-gray-900">{submission.firm_name}</h1>
            <span className={`text-xs px-2 py-1 rounded font-medium ${statusCfg.className}`}>
              {statusCfg.label}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            <Link href={`/candidates/${submission.candidate_id}`} className="hover:text-green-700">
              {submission.candidate_name}
            </Link>
            {submission.candidate_practice_area && <><span className="text-gray-300">·</span><span>{submission.candidate_practice_area}</span></>}
            {submission.candidate_jd_year && <><span className="text-gray-300">·</span><span>JD {submission.candidate_jd_year}</span></>}
          </div>
        </div>
        {!isDone && advanceLabel && (
          <Button onClick={advance} disabled={advancing}
            className="bg-green-700 hover:bg-green-800 text-white shrink-0">
            {advancing ? "Updating..." : advanceLabel} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>

      {/* Progress bar */}
      {!isDone && (
        <div className="flex items-center gap-2 mb-6">
          {STATUS_STEPS.map((step, i) => (
            <div key={step} className="flex items-center gap-2">
              <div className={`h-2 rounded-full transition-colors ${i <= currentStepIdx ? "bg-green-600 w-8" : "bg-gray-200 w-8"}`} />
              {i < STATUS_STEPS.length - 1 && <div className="w-4 h-px bg-gray-200" />}
            </div>
          ))}
          <span className="text-xs text-gray-500 ml-1 capitalize">{submission.status.replace("_", " ")}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main: cover letter + notes */}
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-semibold text-gray-800">Cover Letter</Label>
              <div className="flex items-center gap-2">
                {submission.cover_letter_generated && (
                  <span className="text-xs text-purple-600 flex items-center gap-0.5">
                    <Wand2 className="w-3 h-3" /> AI-generated
                  </span>
                )}
                {ai_assist && coverLetterTimedOut && (
                  <span className="text-xs text-amber-600">Taking longer than expected —</span>
                )}
                {ai_assist && (
                  <button
                    onClick={generateCoverLetter}
                    disabled={generating}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded border border-purple-200 text-purple-700 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {generating
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Generating...</>
                      : <><Wand2 className="w-3 h-3" /> {coverLetterTimedOut ? "Try again" : submission.cover_letter_generated ? "Regenerate" : "Generate"}</>
                    }
                  </button>
                )}
              </div>
            </div>

            {ai_assist && !submission.job_description && (
              <div className="mb-3">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAdvanced((s) => !s)}
                >
                  {showAdvanced ? "Hide" : "Add"} job description / firm list (optional — no linked job, so the letter is generic unless you provide one)
                </Button>
                {showAdvanced && (
                  <div className="mt-2 space-y-2">
                    <Textarea
                      value={jobDescription}
                      onChange={(e) => setJobDescription(e.target.value)}
                      rows={4}
                      placeholder="Paste a job description to tailor the letter to this opportunity..."
                      className="text-sm"
                    />
                    <Input
                      value={firmListInput}
                      onChange={(e) => setFirmListInput(e.target.value)}
                      placeholder="Firm names, comma-separated (optional — broadens the letter's framing)"
                      className="text-sm"
                    />
                  </div>
                )}
              </div>
            )}

            <Textarea
              value={coverLetter}
              onChange={(e) => setCoverLetter(e.target.value)}
              rows={14}
              className="font-mono text-sm"
              placeholder="Cover letter text will appear here after generation, or type directly..."
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-5">
            <Label className="text-base font-semibold text-gray-800 mb-3 block">Notes</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4}
              placeholder="Internal notes about this submission..." />
          </div>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-green-700 hover:bg-green-800 text-white">
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Submission method */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Method</p>
            <div className="flex gap-2">
              {["email_only", "portal_plus_email"].map((m) => (
                <button key={m} onClick={() => setMethod(m)}
                  className={`flex-1 text-xs py-2 rounded border font-medium transition-colors ${
                    method === m ? "bg-green-700 text-white border-green-700" : "border-gray-200 text-gray-500 hover:border-green-400"
                  }`}>
                  {m === "email_only" ? "Email Only" : "Portal + Email"}
                </button>
              ))}
            </div>
          </div>

          {/* Firm contact */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Firm Contact</p>
            <div>
              <Label className="text-xs text-gray-500">Name</Label>
              <Input value={contactName} onChange={(e) => setContactName(e.target.value)}
                placeholder="Contact name" className="mt-1 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Email</Label>
              <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
                placeholder="contact@firm.com" className="mt-1 text-sm" type="email" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Proxy Email</Label>
              <Input value={proxyEmail} onChange={(e) => setProxyEmail(e.target.value)}
                placeholder="proxy@firm.com" className="mt-1 text-sm" type="email" />
            </div>
          </div>

          {/* Job */}
          {submission.job_id ? (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Linked Job</p>
              <p className="text-sm font-medium text-gray-800">{submission.job_title || "Untitled"}</p>
              <Link href={`/job-alerts/${submission.job_id}`}
                className="text-xs text-green-700 hover:underline flex items-center gap-1 mt-1">
                <Briefcase className="w-3 h-3" /> View job details
              </Link>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Linked Job</p>
              <p className="text-xs text-gray-400">No job linked — SKM or portal submission</p>
            </div>
          )}

          {/* Dates */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Timeline</p>
            <div className="space-y-1.5 text-xs text-gray-500">
              <div className="flex justify-between"><span>Created</span><span>{fmt(submission.created_at)}</span></div>
              <div className="flex justify-between"><span>Submitted</span><span>{fmt(submission.submitted_at)}</span></div>
              {submission.submitted_by && <div className="flex justify-between"><span>By</span><span>{submission.submitted_by}</span></div>}
            </div>
          </div>

          {/* Approval */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Approval</p>
            <button
              onClick={toggleApprove}
              className={`w-full flex items-center justify-center gap-2 py-2 rounded border text-sm font-medium transition-colors ${
                submission.candidate_approved
                  ? "bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                  : "border-gray-200 text-gray-500 hover:border-green-400 hover:text-green-700"
              }`}
            >
              {submission.candidate_approved
                ? <><CheckCircle2 className="w-4 h-4" /> Approved</>
                : <><Circle className="w-4 h-4" /> Mark Approved</>
              }
            </button>
            {submission.candidate_approved && (
              <p className="text-xs text-gray-400 mt-1.5 text-center">Ready to submit</p>
            )}
          </div>

          {/* Export */}
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Export</p>
            <div className="flex gap-2">
              <a
                href={`/submissions/${submission.id}/download_pdf`}
                className="flex-1 flex items-center justify-center gap-1 text-xs border border-gray-200 text-gray-600 rounded py-2 hover:border-green-400 hover:text-green-700 transition-colors"
              >
                <FileText className="w-3.5 h-3.5" /> PDF
              </a>
              <a
                href={`/submissions/${submission.id}/download_docx`}
                className="flex-1 flex items-center justify-center gap-1 text-xs border border-gray-200 text-gray-600 rounded py-2 hover:border-green-400 hover:text-green-700 transition-colors"
              >
                <FileText className="w-3.5 h-3.5" /> DOCX
              </a>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
